/** Examples-only prompt for POST /api/check-region-needed. Picker UX is unchanged. */
export const REGION_NEEDED_SYSTEM_PROMPT = `Determine if a product search query requires province/state/region specificity WITHIN the same country to provide accurate recommendations.

IMPORTANT: Only return true for products where recommendations vary significantly by province/state/region WITHIN the same country (not country-to-country differences).

Examples that NEED region (return true):
- "winter tires" / "pneus d'hiver" → Climate and provincial law change the compound, stud rules, and which local shops stock what
- "winter parka" / "parka d'hiver" → Regional climate and local brands (Kanuk, Quartz Co. in Quebec) change the shortlist
- "maple syrup" / "sirop d'érable" → Production, grades, and brands are regional (Quebec vs the rest of Canada)
- "water softener" / "adoucisseur d'eau" → Water hardness varies by region (Calgary hard, Vancouver soft)
- "internet modem" → ISP compatibility varies by region (Videotron Quebec, Shaw BC)
- "cell phone plan" → Carrier coverage and offers vary by province/state
- "radon detector" → Radon levels vary by geological region
- "dehumidifier vs humidifier" → Climate varies within country (coastal BC vs dry Alberta)
- "propane grill" → Gas availability and rules vary by municipality
- "tax software" → Provincial/state tax rules differ
- "water heater" / other bulky installed goods → Code, fuel type, and installer networks are regional

Examples that DO NOT need region (return false):
- "iPhone" / "Pixel" / phones / "téléphone" → Globally consolidated; the same devices ship nationwide
- "laptop" / "ordinateur portable" → Standard product, no regional variance
- "GPU" / "graphics card" → Globally consolidated hardware
- "headphones" / "écouteurs" → Universal product
- "coffee maker" → No regional dependency
- "running shoes" → No regional requirements
- "air conditioner" → Size depends on home, not province
- "high pad" / "desk pad" / "mattress topper" → Generic consumer products, no regional variance

Return JSON:
{
  "needsRegion": boolean,
  "confidence": 0.0-1.0,
  "reason": "Brief explanation of why region matters or doesn't matter"
}`;

export type RegionCheckResult = {
  needsRegion: boolean;
  confidence: number;
  reason: string;
};

/** Climate / bulky / local-brand queries that should still prompt when the LLM fails. */
const REGION_NEEDED_FALLBACK_RE =
  /\b(winter tires?|pneus d['’]hiver|pneus hiver|winter parka|parka d['’]hiver|maple syrup|sirop d['’]erable|water softener|adoucisseur|radon)\b/i;

export const fallbackRegionNeeded = (query: string): boolean =>
  REGION_NEEDED_FALLBACK_RE.test(query.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));

export const parseRegionCheckResult = (raw: unknown): RegionCheckResult | null => {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.needsRegion !== 'boolean') return null;
  const confidence =
    typeof o.confidence === 'number' && Number.isFinite(o.confidence)
      ? Math.min(1, Math.max(0, o.confidence))
      : 0;
  const reason = typeof o.reason === 'string' ? o.reason : '';
  return { needsRegion: o.needsRegion, confidence, reason };
};
